import { useState, useCallback, useRef } from "react"
import {
  fetchProjects,
  fetchProject,
  createProject,
  updateProject,
  deleteProject,
} from "../api/projectsApi"

// ── useProjects ──────────────────────────────────────────────────────────────
// Fetches the project list with pagination / filter support.
export function useProjects() {
  const [data, setData]       = useState(null) // { projects, meta }
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)
  const abortRef              = useRef(null)

  const fetch = useCallback(async (filters = {}) => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()

    setLoading(true)
    setError(null)
    try {
      const result = await fetchProjects(filters)
      setData(result)
    } catch (err) {
      if (err.name !== "AbortError") setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  const refresh = useCallback((filters) => fetch(filters), [fetch])

  return { data, loading, error, fetch, refresh }
}

// ── useProject ───────────────────────────────────────────────────────────────
// Fetches a single project (with availability summary).
export function useProject() {
  const [data, setData]       = useState(null) // { project }
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)

  const fetch = useCallback(async (id) => {
    setLoading(true)
    setError(null)
    try {
      const result = await fetchProject(id)
      setData(result)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  return { data, loading, error, fetch }
}

// ── Mutations ──────────────────────────────────────────────────────────────
function useMutation(fn) {
  const [status, setStatus] = useState("idle")
  const [error, setError]   = useState(null)

  const mutate = useCallback(async (...args) => {
    setStatus("pending")
    setError(null)
    try {
      const result = await fn(...args)
      setStatus("success")
      return result
    } catch (err) {
      const msg = err.response?.data?.errors || [err.message || "Something went wrong"]
      setError(msg)
      setStatus("error")
      throw msg
    }
  }, [])

  return {
    mutate,
    status,
    error,
    isPending: status === "pending",
    reset: () => { setStatus("idle"); setError(null) },
  }
}

export const useCreateProject = () => useMutation(createProject)
export const useUpdateProject = () => useMutation((id, payload, images) => updateProject(id, payload, images))
export const useDeleteProject = () => useMutation(deleteProject)
